import { useMemo } from "react";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../dynamicGrid";
import { EmptyState } from "../shared/EmptyState";
import { PageTitle } from "../shared/Page";
import { HasPermission } from "../shared/permissions";
import { PersonIcon3, RegistrationControllerIcon2 } from "../shared/icons";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { trackEvent } from "../shared/Snowplow";
import { prefetch } from "../api/prefetch";
import { REGISTRATION_METADATA_FIELDS, type GridViewRegistration } from "../api/registrations.types";
import type { PersonalDataControllerListItem } from "../api/personalDataControllers";
import { selectPartnerOptionsQueryOptions } from "../api/partners";
import { enterpriseCustomerOrganisationsQueryOptions } from "../api/enterpriseCustomer";
import { useGetUsersFromCurrentOrganisation } from "../api/users";
import {
  sharedProcessingByIdQueryOptions,
  sharedProcessingFormQueryOptions,
  sharedProcessingsGridViewQueryOptions,
  updateSharedProcessingsGridViewFiltersMutationOptions,
} from "../api/sharedProcessings";
import { CreateSharedProcessingDialog } from "./CreateSharedProcessingDialog";
import { SharedProcessingInlineFormDrawer } from "./SharedProcessingDrawer";
import { SharedProcessingGuidedDrawer } from "./SharedProcessingGuidedDrawer";
import { SharedProcessingHistoryDrawer } from "./SharedProcessingHistoryDrawer";
import { SharedProcessingShareSubDrawer } from "./SharedProcessingShareSubDrawer";
import { SharedProcessingActionMenu } from "./SharedProcessingDrawerHeader";

const SharedProcessingsGridPage = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  usePrivacyDocumentTitle("RegistrationGrid.Shared.Title");
  const gridViewQuery = useQuery(sharedProcessingsGridViewQueryOptions());
  const formQuery = useQuery(sharedProcessingFormQueryOptions());
  const updateFiltersMutation = useMutation(updateSharedProcessingsGridViewFiltersMutationOptions());
  const usersQuery = useGetUsersFromCurrentOrganisation();
  const partnersQuery = useQuery(selectPartnerOptionsQueryOptions());
  const organisationsQuery = useQuery(enterpriseCustomerOrganisationsQueryOptions());

  return (
    <DynamicGrid.Grid
      rowsData={gridViewQuery.data?.registrations}
      form={formQuery.data}
      getRowId={getRowId}
      asyncQuestionCardData={useMemo(
        () => ({
          usersQueryData: usersQuery.data,
          partnersQueryData: partnersQuery.data,
          enterpriseCustomerOrganisationsQueryData: organisationsQuery.data,
          personalDataControllersQueryData: NO_PERSONAL_DATA_CONTROLLERS_IN_ENTERPRISE_ROOT_SCOPE,
        }),
        [usersQuery.data, partnersQuery.data, organisationsQuery.data]
      )}
      savedFiltersProps={{
        stringifiedFilters: gridViewQuery.data?.stringifiedFilters,
        onChange: stringifiedFilters => {
          trackEvent.changeRegistrationSavedFilters();
          updateFiltersMutation.mutate(stringifiedFilters);
        },
      }}
      onRowSelect={id => navigate(paths.sharedProcessingsInline(id))}
      defaultSort={{ field: REGISTRATION_METADATA_FIELDS.UPDATED_AT, sort: "desc" }}
      Toolbar={SharedProcessingsToolbar}
      Footer={SharedProcessingsGridFooter}
      mainColumnProps={{
        headerName: t("RegistrationGrid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps }) => <RegistrationControllerIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => [
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.CreatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.CREATED_AT,
        }),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.UpdatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.UPDATED_AT,
        }),
        responsibleColumn({ headerName: t("RegistrationGrid.ResponsibleHeader") }),
        ...questionCardColumns,
      ]}
      NoRowsOverlay={NoSharedProcessingsOverlay}
      texts={{ searchFieldPlaceholder: t("RegistrationGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <SharedProcessingActionMenu
          sharedProcessingId={row.registrationId}
          onDeleted={() => {
            if (selectedRowId === row.registrationId) navigate(paths.sharedProcessingsGrid());
          }}
          onCopied={id => navigate(paths.sharedProcessingsInline(id))}
        />
      )}
    />
  );
};

const getRowId = (row: GridViewRegistration) => row.registrationId;

const NO_PERSONAL_DATA_CONTROLLERS_IN_ENTERPRISE_ROOT_SCOPE: Array<PersonalDataControllerListItem> = [];

const responsibleColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.RESPONSIBLE }),
  width: 280,
  valueGetter: (_, row) => `${row.responsible.firstName} ${row.responsible.lastName}`,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.TextCellContent
        value={`${row.responsible.firstName} ${row.responsible.lastName}`}
        Icon={PersonIcon3}
      />
    </DynamicGrid.Cell>
  ),
});

const SharedProcessingsGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="RegistrationGrid.NumberOfRows" />;

const NoSharedProcessingsOverlay = () => <EmptyState labelKey="RegistrationGrid.NoRows" />;

const SharedProcessingsToolbar = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Toolbar
      pageTitle={
        <PageTitle title={t("RegistrationGrid.Shared.Title")} subTitle={t("RegistrationGrid.Shared.SubTitle")} />
      }
      actionButtons={
        <HasPermission permission="nova-forms-api:enterprise-customers:registrations:manage">
          <DynamicGrid.CreateNewButton
            labelKey="CreateRegistration.Button"
            renderDialog={({ onClose }) => (
              <CreateSharedProcessingDialog
                onClose={onClose}
                onCreated={id => navigate(paths.sharedProcessingsInline(id))}
              />
            )}
          />
        </HasPermission>
      }
    />
  );
};

export const prefetchSharedProcessingsGridPage = () => {
  prefetch(sharedProcessingsGridViewQueryOptions());
  prefetch(sharedProcessingFormQueryOptions());
  prefetch(enterpriseCustomerOrganisationsQueryOptions());
};

export const sharedProcessingsGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <SharedProcessingsGridPage />,
  rowDrawerRoutes: [
    { index: true, element: <SharedProcessingInlineFormDrawer /> },
    { path: "guided/*", element: <SharedProcessingGuidedDrawer /> },
    { path: "history", element: <SharedProcessingHistoryDrawer /> },
    { path: "share", element: <SharedProcessingShareSubDrawer /> },
  ],
  gridPrefetcher: prefetchSharedProcessingsGridPage,
  rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetch(sharedProcessingByIdQueryOptions(rowId)),
});
